import React, { useMemo } from 'react'
import { Dropdown, Option, OptionGroup, makeStyles, tokens } from '@fluentui/react-components'
import { Control, Controller } from 'react-hook-form'
import { Clause } from '../../../api/schemas/schema'
import { t } from '../../../i18n/i18n'
import { Field } from './Field'
import { ConditionalTooltip } from '../other/ConditionalTooltip'

const useStyles = makeStyles({
   allOption: {
      fontWeight: tokens.fontWeightBold,
   },
   optionGroupHeader: {
      fontWeight: tokens.fontWeightSemibold,
      color: tokens.colorNeutralForeground1,
   },
   subOption: {
      paddingLeft: '24px',
   },
})

type ControlledClauseDropdownProps = {
   name: string
   control: Control<any>
   label: string
   options: Clause[]
   disabled?: boolean
   showTooltip?: boolean
   tooltipMsg?: string
}

export const ControlledClauseDropdown = ({ name, control, label, options, disabled, showTooltip, tooltipMsg }: ControlledClauseDropdownProps) => {
   const classes = useStyles()

   const groupedClauses = useMemo(() => {
      const groups: Record<number, { parent?: Clause; children: Clause[] }> = {}

      options.forEach((clause) => {
         const main = clause.mainNumber || 0
         groups[main] ??= { children: [] }

         if (!clause.subNumber || clause.subNumber === 0) {
            groups[main].parent = clause
         } else {
            groups[main].children.push(clause)
         }
      })

      return Object.entries(groups).sort(([a], [b]) => Number(a) - Number(b))
   }, [options])

   const allIds = useMemo(() => options.map((o) => o.id!), [options])

   return (
      <Controller
         name={name}
         control={control}
         render={({ field: { value = [], onChange }, fieldState: { error } }) => {
            const selectedIds = (value as Clause[]).map((c) => c.id!)
            const isAllSelected = allIds.length > 0 && selectedIds.length === allIds.length

            const setSelection = (ids: string[]) => {
               const selectedObjects = options.filter((o) => ids.includes(o.id!))
               onChange(selectedObjects)
            }

            const getDisplayValue = () => {
               if (selectedIds.length === 0) return ''
               if (isAllSelected) return t('All')
               return (value as Clause[]).map((c) => `${c.text}`).join(', ')
            }

            return (
               <Field label={label} validationMessage={error?.message ?? null}>
                  <ConditionalTooltip show={showTooltip} content={tooltipMsg}>
                     <Dropdown
                        multiselect
                        disabled={disabled}
                        selectedOptions={isAllSelected ? [...selectedIds, 'all-toggle'] : selectedIds}
                        value={getDisplayValue()}
                        onOptionSelect={(_, data) => {
                           if (data.optionValue !== 'all-toggle') {
                              setSelection(data.selectedOptions.filter((id) => id !== 'all-toggle'))
                           }
                        }}
                     >
                        <OptionGroup>
                           <Option
                              key="all-toggle"
                              value="all-toggle"
                              className={classes.allOption}
                              onClick={(e) => {
                                 e.stopPropagation()
                                 setSelection(isAllSelected ? [] : allIds)
                              }}
                           >
                              {t('All')}
                           </Option>
                        </OptionGroup>

                        {groupedClauses.map(([mainNumber, group]) => {
                           const parent = group.parent
                           const children = group.children
                           const groupIds = [...(parent ? [parent.id!] : []), ...children.map((c) => c.id!)]
                           const isGroupFullySelected = groupIds.every((id) => selectedIds.includes(id))

                           return (
                              <OptionGroup key={mainNumber}>
                                 {parent && (
                                    <Option
                                       className={classes.optionGroupHeader}
                                       value={parent.id!}
                                       onClick={(e) => {
                                          e.stopPropagation()
                                          const next = isGroupFullySelected
                                             ? selectedIds.filter((id) => !groupIds.includes(id))
                                             : Array.from(new Set([...selectedIds, ...groupIds]))
                                          setSelection(next)
                                       }}
                                    >
                                       {parent.text}
                                    </Option>
                                 )}

                                 {children.map((child) => (
                                    <Option key={child.id} value={child.id!} className={classes.subOption}>
                                       {child.text}
                                    </Option>
                                 ))}
                              </OptionGroup>
                           )
                        })}
                     </Dropdown>
                  </ConditionalTooltip>
               </Field>
            )
         }}
      />
   )
}
